import { Injectable } from "@angular/core";
import { Actions, Effect, ofType } from "@ngrx/effects";
import { switchMap, withLatestFrom, mergeMap, tap, catchError } from "rxjs/operators";
import { EMPTY, of, throwError } from "rxjs";
import {
    EBenefitsActions,
    SetCards,
    SetFilters,
    SetGeneratedCodeCards,
    SetLoadedCards,
    SelectFilters,
    SetCardTypes,
    GetGeneratedCodes,
    GetCodeValidaty,
    GetCounterGeneratedCodes,
    SetExclusiveBenefitsCards,
    SetBenefitsUsersDetails,
    GetTransactions,
    SetBenefitsDistricts,
    GetBenefitsUsersDetails,
    AddPageCounter,
    SetErrorBenefits,
} from "@edp-store/actions/benefits.actions";
import {
    BenefitsInfo,
    ICard,
    Card,
    BenefitsFactory,
    FiltersFactory,
    Filter,
    CardType,
    ExclusiveBenefitsCardFactory,
    ExclusiveBenefitsCard,
    IBenefitsUserDetails,
    BenefitsDistricts,
} from "@edp-models/benefits.model";
import { BenefitsService } from "@edp-services/benefits.service";
import { Store } from "@ngrx/store";
import { IAppState } from "@edp-store/state/app.state";
import { getRouterState } from "@edp-store/selectors/router.selectors";
import { SetDashboardGeneratedCodeCards } from "@edp-store/actions/edpe.actions";
import { getCardTypes, getUserBenefit } from "@edp-store/selectors/benefits.selectors";
import { TranslateService } from "@ngx-translate/core";
import { ELayoutActions, ShowLoader } from "@edp-store/actions/layout.actions";
import { getContract } from "@edp-store/selectors/contract.selectors";
import { Contract } from "@edp-components/edp-contract";

@Injectable()
export class BenefitsEffects {
    @Effect()
    loadAvailable$ = this.actions$.pipe(
        ofType(EBenefitsActions.GetAvailable),
        withLatestFrom(this.store.select(getUserBenefit)),
        switchMap(([action, userBenefit]: [any, IBenefitsUserDetails]) => {
            return this.benefitsService.getAvailable({ pn: userBenefit?.pn });
        }),
        catchError(err => {
            return of(err);
        }),
        mergeMap((benefitsInfo: BenefitsInfo) => {
            if (benefitsInfo instanceof Error) {
                this.store.dispatch(new ShowLoader(false));
                return of(new SetErrorBenefits(true));
            }
            const [list, generated, exclusiveBenefitsCards]: Card[][] = this.getCards(benefitsInfo.benefits, benefitsInfo.cardTypes);
            const exclusiveBenefitsCard = this.getExclusiveBenefitsCards(benefitsInfo.benefits);

            return of(
                new SetExclusiveBenefitsCards(exclusiveBenefitsCard),
                new SetCards(list),
                new SetErrorBenefits(false),
                new GetGeneratedCodes(),
                new SetDashboardGeneratedCodeCards(generated),
                new SetCardTypes(benefitsInfo.cardTypes),
                new SelectFilters(),
                new SetLoadedCards(true),
                new ShowLoader(false)
            );
        })
    );

    @Effect()
    refreshCards$ = this.actions$.pipe(
        ofType(EBenefitsActions.RefreshCards),
        switchMap(() => this.benefitsService.getAvailable()),
        mergeMap((benefitsInfo: BenefitsInfo) => {
            const [list, generated]: Card[][] = this.getCards(benefitsInfo.benefits, benefitsInfo.cardTypes);
            return of(new SetCards(list), new GetGeneratedCodes(), new SetDashboardGeneratedCodeCards(generated));
        })
    );

    @Effect()
    selectFilters$ = this.actions$.pipe(
        ofType(EBenefitsActions.SelectFilters),
        withLatestFrom(this.store.select(getCardTypes), this.store.select(getRouterState)),
        switchMap(([action, cardTypes, router]: [SelectFilters, CardType[], any]) => {
            const filtersList: Filter[] = FiltersFactory.generateFilters(cardTypes);

            const queryParams = router.state.queryParams;

            if (Object.keys(queryParams).length > 0) {
                Object.keys(queryParams).forEach(param => {
                    if (["Area", "Type", "Status"].includes(param)) {
                        const queryFilter = filtersList.find(filter => filter.urlName === param);
                        queryFilter.hasOptionSelected = true;
                        queryFilter.allSelected = false;
                        this.selectFilterOption(queryParams[param], queryFilter);
                    }
                });
            }

            return of(new SetFilters(filtersList));
        })
    );

    @Effect()
    loadGeneratedCodes$ = this.actions$.pipe(
        ofType(EBenefitsActions.GetGeneratedCodes),
        tap(() => this.store.dispatch(new ShowLoader(true))),
        switchMap(() => this.benefitsService.getGeneratedCodes()),
        mergeMap((partnerCodes: ICard[]) => {
            const generated: Card[] = partnerCodes.map(partner => BenefitsFactory.createGeneratedCode(partner, partner.generatedCode));

            return of(new SetGeneratedCodeCards(generated), new GetCounterGeneratedCodes(), new ShowLoader(false));
        })
    );

    @Effect()
    loadCounterGeneratedCodes$ = this.actions$.pipe(
        ofType(EBenefitsActions.GetCounterGeneratedCodes),
        switchMap(() =>
            this.benefitsService.getCounterPartnerBenefits().pipe(
                switchMap(response => {
                    return [{ type: EBenefitsActions.SetCounterGeneratedCodes, payload: response }];
                }),
                catchError((error: Error) => EMPTY)
            )
        )
    );

    @Effect()
    loadBenefitsUser$ = this.actions$.pipe(
        ofType(EBenefitsActions.GetBenefitsUsersDetails),
        switchMap(() =>
            this.benefitsService.getBenefitsUsersList().pipe(
                switchMap((response: IBenefitsUserDetails[]) => {
                    return of(new SetBenefitsUsersDetails(response));
                }),
                catchError((error: Error) => EMPTY)
            )
        )
    );

    @Effect()
    getCodeValidaty$ = this.actions$.pipe(
        ofType(EBenefitsActions.GetCodeValidaty),
        switchMap((action: GetCodeValidaty) => {
            this.store.dispatch(new ShowLoader(true));
            return this.benefitsService.validateCodeBP(action.payload).pipe(
                switchMap((response: any) => [
                    { type: EBenefitsActions.SetCodeValidaty, payload: response.code },
                    { type: ELayoutActions.ShowLoader, payload: false },
                ]),
                catchError(() => [{ type: ELayoutActions.ShowLoader, payload: false }])
            );
        })
    );

    @Effect()
    getTransactions$ = this.actions$.pipe(
        ofType(EBenefitsActions.GetTransactions),
        switchMap((action: GetTransactions) => {
            this.store.dispatch(new ShowLoader(true));
            return this.benefitsService.getTransactionList(action.payload).pipe(
                switchMap((response: any) => {
                    return [
                        { type: EBenefitsActions.SetTransactions, payload: response.data?.movements || [] },
                        { type: EBenefitsActions.SetBalance, payload: response.data?.userBalance },
                        { type: ELayoutActions.ShowLoader, payload: false },
                    ];
                }),
                catchError(() => [
                    { type: EBenefitsActions.SetTransactions, payload: [] },
                    { type: EBenefitsActions.SetBalance, payload: null },
                    { type: ELayoutActions.ShowLoader, payload: false },
                ])
            );
        })
    );

    @Effect()
    getDistricts$ = this.actions$.pipe(
        ofType(EBenefitsActions.GetBenefitsDistricts),
        withLatestFrom(this.store.select(getContract)),
        switchMap(([action, contract]: [any, Contract]) =>
            this.benefitsService.getDistricts({ contractId: contract.ContractId }).pipe(
                switchMap((response: BenefitsDistricts[]) => {
                    return of(new SetBenefitsDistricts(response));
                }),
                catchError((error: Error) => EMPTY)
            )
        )
    );

    @Effect()
    addPageCounter$ = this.actions$.pipe(
        ofType(EBenefitsActions.AddPageCounter),
        switchMap((action: AddPageCounter) =>
            this.benefitsService.addPageCounter(action.payload).pipe(
                switchMap(() => [{ type: EBenefitsActions.GetBenefitsUsersDetails }, { type: EBenefitsActions.SetPageCounter }]),
                catchError((error: Error) => EMPTY)
            )
        )
    );

    constructor(
        private actions$: Actions,
        private benefitsService: BenefitsService,
        private store: Store<IAppState>,
        private translate: TranslateService
    ) {}

    private getCards(cards: ICard[], cardTypes: CardType[]): Card[][] {
        const cardTypeCodes: string[] = [];
        cardTypes.forEach(cardType => (cardTypeCodes[cardType.id] = cardType.code));

        const cardList: Card[] = [];
        const generatedCodeCardList: Card[] = [];
        cards.forEach(card => {
            const partners = card.partners ? card.partners.length : 0;
            if (card.categoryId) {
                if (partners === 1) {
                    cardList.push(BenefitsFactory.create(card.partners[0]));
                } else if (partners > 1) {
                    cardList.push(BenefitsFactory.create(card));
                }
            } else {
                cardList.push(BenefitsFactory.create(card));
            }

            const excludeTypesToGeneratedCardList = [6]; // 6 - MultiCode Partners
            if (!excludeTypesToGeneratedCardList.includes(card.type.value)) {
                if (partners > 0) {
                    card.partners.forEach(prtn => {
                        if (prtn.generatedCodes && prtn.generatedCodes.length > 0) {
                            prtn.generatedCodes.forEach(generatedCode =>
                                generatedCodeCardList.push(BenefitsFactory.createGeneratedCode(prtn, generatedCode))
                            );
                        }
                    });
                } else {
                    card.generatedCodes.forEach(generatedCode =>
                        generatedCodeCardList.push(BenefitsFactory.createGeneratedCode(card, generatedCode))
                    );
                }
            }
        });

        return [cardList, generatedCodeCardList];
    }

    private getExclusiveBenefitsCards(cards: ICard[]): ExclusiveBenefitsCard[] {
        const exlusiveBenefits = cards.filter(item => item.pushPacksEdp === "1");
        const exclusiveBenefitsCards = exlusiveBenefits.map(item => ExclusiveBenefitsCardFactory.create(item));
        return exclusiveBenefitsCards;
    }

    private selectFilterOption(params: string | [string], filter: Filter) {
        if (Array.isArray(params)) {
            params.forEach(option => {
                filter.options.find(
                    opt => (filter.urlName === "Status" ? this.translate.instant(opt.name) : opt.name) === option
                ).selected = true;
            });
        } else {
            filter.options.find(
                opt => (filter.urlName === "Status" ? this.translate.instant(opt.name) : opt.name) === params
            ).selected = true;
        }
    }
}
